import { ScoreData } from 'game-logic';
import { Board } from './board';
import { Card } from './card/card';
import { CardScoreRecord } from './card/card-score-record';
import { Pile } from './card/pile';
import { PileType, PointsType } from './logic-types';
import { OnScoreChangedAction } from './solitaire-game-logic';

export interface SolitaireScoreData extends ScoreData {
  moves: number;
  cardsInFoundation: number;
}

export class GameLogicScore {
  private readonly scoreBonusMultiplier: number = 5;

  public get currentScore(): number {
    return this.score;
  }

  public get currentMoves(): number {
    return this.moves;
  }

  public set onScoreChangedAction(value: OnScoreChangedAction) {
    this.scoreChangedAction = value;
  }

  private score: number;
  private moves: number;
  private boostMulti: number;
  private cardScoreRecordList: CardScoreRecord[];
  private scoreChangedAction?: OnScoreChangedAction;

  constructor() {
    this.score = 0;
    this.moves = 0;
    this.boostMulti = 0;
    this.cardScoreRecordList = [];
  }

  public generateScoreData(board: Board): SolitaireScoreData {
    return {
      score: this.score,
      moves: this.moves,
      cardsInFoundation: board.foundations.reduce((acc, pile) => acc + pile.cardCount, 0),
    };
  }

  public addPoints(newPoints: number, _pointsType: PointsType, _card: Card | undefined): void {
    if (newPoints === 0) {
      return;
    }

    this.score += newPoints;

    if (this.score < 0) {
      this.score = 0;
    }

    this.scoreChangedAction?.(this.score);
  }

  public addMove(): void {
    this.moves++;
  }

  public grantPoints(card: Card, pile: Pile): void {
    if (pile.type === PileType.Waste) {
      return;
    }

    if (card.cardId === undefined) {
      return;
    }

    // Check if we have a score for this specific card
    let cardScoreRecord = this.cardScoreRecordList.find((cardScoreRecord) =>
      cardScoreRecord.matchesCardId(card),
    );
    if (cardScoreRecord === undefined) {
      cardScoreRecord = new CardScoreRecord(card.cardId, card.index);
      this.cardScoreRecordList.push(cardScoreRecord);
    }

    const oldPlacedOnFoundation = cardScoreRecord.placedOnFoundation;

    // Add the final points
    let finalPoints = cardScoreRecord.saveMoveAndReturnNewPoints(pile.type);

    const firstTimePlacedOnFoundation =
      !oldPlacedOnFoundation && cardScoreRecord.placedOnFoundation;

    // Calculate boost
    if (finalPoints > 0 && pile.type != PileType.Foundation) {
      this.boostMulti = 0;
    }

    const boost = this.boostMulti * this.scoreBonusMultiplier;

    if (pile.type === PileType.Foundation && finalPoints > 0) {
      finalPoints += boost;
    }

    this.addPoints(finalPoints, PointsType.Move, card);

    if (firstTimePlacedOnFoundation) {
      this.boostMulti++;
    }
  }
}
